import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import './AdminDashboard.css';

const NeighborhoodManagementTab = () => {
  // State variables
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    state: '',
    walkScore: 50,
    transitScore: 50,
    bikeScore: 50,
    schools: {
      elementary: { name: '', rating: 5 },
      middle: { name: '', rating: 5 },
      high: { name: '', rating: 5 }
    },
    highlights: [],
    description: '',
    image: ''
  });
  
  // Temporary highlight form state
  const [highlightText, setHighlightText] = useState('');
  const [highlightIcon, setHighlightIcon] = useState('info');
  
  // Fetch neighborhoods on component mount
  useEffect(() => {
    fetchNeighborhoods();
  }, []);
  
  // Fetch neighborhoods from API
  const fetchNeighborhoods = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await propertyService.getNeighborhoods();
      
      if (response && response.data) {
        setNeighborhoods(response.data);
      } else {
        setError('Failed to load neighborhoods: Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching neighborhoods:', err);
      setError(`Error loading neighborhoods: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Open modal for creating new neighborhood
  const handleAddNeighborhood = () => {
    setIsEditing(false);
    setFormData({
      name: '',
      city: '',
      state: '',
      walkScore: 50,
      transitScore: 50,
      bikeScore: 50,
      schools: {
        elementary: { name: '', rating: 5 },
        middle: { name: '', rating: 5 },
        high: { name: '', rating: 5 }
      },
      highlights: [],
      description: '',
      image: ''
    });
    setSelectedNeighborhood(null);
    setActiveTab('details');
    setShowModal(true);
  };
  
  // Open modal for editing existing neighborhood
  const handleEditNeighborhood = async (id) => {
    setLoading(true);
    
    try {
      const response = await propertyService.getNeighborhood(id);
      
      if (response && response.data) {
        const neighborhoodData = response.data;
        
        setFormData({
          id: neighborhoodData._id,
          name: neighborhoodData.name || '',
          city: neighborhoodData.city || '',
          state: neighborhoodData.state || '',
          walkScore: neighborhoodData.walkScore || 50,
          transitScore: neighborhoodData.transitScore || 50,
          bikeScore: neighborhoodData.bikeScore || 50,
          schools: {
            elementary: neighborhoodData.schools?.elementary || { name: '', rating: 5 },
            middle: neighborhoodData.schools?.middle || { name: '', rating: 5 },
            high: neighborhoodData.schools?.high || { name: '', rating: 5 }
          },
          highlights: neighborhoodData.highlights || [],
          description: neighborhoodData.description || '',
          image: neighborhoodData.image || ''
        });
        
        setSelectedNeighborhood(neighborhoodData);
        setIsEditing(true);
        setActiveTab('details');
        setShowModal(true);
      } else {
        setError('Failed to load neighborhood details: Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching neighborhood details:', err);
      setError(`Error loading neighborhood details: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle neighborhood deletion
  const handleDeleteNeighborhood = async (id) => {
    if (!id) return;
    
    try {
      await propertyService.deleteNeighborhood(id);
      
      setFeedbackMessage({
        type: 'success',
        message: 'Neighborhood deleted successfully'
      });
      
      setShowDeleteConfirm(false);
      await fetchNeighborhoods();
      
      setTimeout(() => {
        setFeedbackMessage({ type: '', message: '' });
      }, 3000);
    } catch (err) {
      console.error('Error deleting neighborhood:', err);
      setFeedbackMessage({
        type: 'danger',
        message: `Error deleting neighborhood: ${err.message || 'Unknown error'}`
      });
    }
  };
  
  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle school input changes
  const handleSchoolChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      schools: {
        ...prev.schools,
        [type]: {
          ...prev.schools[type],
          [field]: value
        }
      }
    }));
  };
  
  // Add highlight to list
  const handleAddHighlight = () => {
    if (!highlightText.trim()) {
      setFeedbackMessage({
        type: 'danger',
        message: 'Please enter highlight text'
      });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      highlights: [
        ...prev.highlights,
        {
          icon: highlightIcon,
          text: highlightText.trim()
        }
      ]
    }));
    
    setHighlightText('');
    setHighlightIcon('info');
    
    setFeedbackMessage({
      type: 'success',
      message: 'Highlight added'
    });
    
    setTimeout(() => {
      setFeedbackMessage({ type: '', message: '' });
    }, 3000);
  };
  
  // Remove highlight from list
  const handleRemoveHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };
  
  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.city || !formData.state) {
      setFeedbackMessage({
        type: 'danger',
        message: 'Please fill in all required fields'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      
      const requestData = {
        name: formData.name,
        city: formData.city,
        state: formData.state,
        walkScore: formData.walkScore,
        transitScore: formData.transitScore,
        bikeScore: formData.bikeScore,
        schools: formData.schools,
        highlights: formData.highlights,
        description: formData.description,
        image: formData.image
      };
      
      if (isEditing && formData.id) {
        response = await propertyService.updateNeighborhood(formData.id, requestData);
      } else {
        response = await propertyService.createNeighborhood(requestData);
      }
      
      if (response) {
        setFeedbackMessage({
          type: 'success',
          message: isEditing ? 'Neighborhood updated successfully' : 'Neighborhood created successfully'
        });
        
        setShowModal(false);
        await fetchNeighborhoods();
        
        setTimeout(() => {
          setFeedbackMessage({ type: '', message: '' });
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setFeedbackMessage({
        type: 'danger',
        message: `Error: ${err.message || 'Operation failed'}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Filter neighborhoods based on search query
  const filteredNeighborhoods = neighborhoods.filter(
    item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.state.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Available icon options for highlights
  const iconOptions = [
    'info', 'map-pin', 'shield', 'coffee', 'shopping-bag', 'star',
    'heart', 'activity', 'home', 'tree', 'sun', 'umbrella', 'award'
  ];
  
  return (
    <div style={{ color: '#ffffff' }}>
      <style>{`
        .alert-success {
          background-color: rgba(4, 120, 87, 0.15) !important;
          color: #4ade80 !important;
          border: 1px solid rgba(4, 120, 87, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .alert-danger {
          background-color: rgba(220, 38, 38, 0.15) !important;
          color: #fca5a5 !important;
          border: 1px solid rgba(220, 38, 38, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .close-btn {
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-size: 1.25rem;
          opacity: 0.6;
        }
        .close-btn:hover {
          opacity: 1;
        }
        .filter-header {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          border-radius: 12px;
          padding: 24px !important;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .form-control {
          background-color: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          padding: 10px 12px;
          border-radius: 6px;
          width: 100%;
        }
        .form-control:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          background-color: rgba(255, 255, 255, 0.08) !important;
          outline: none;
        }
        .form-control option {
          background-color: #1a1a1a;
          color: #ffffff;
        }
        .btn-primary {
          background-color: #4f46e5;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-primary:hover {
          background-color: #4338ca;
        }
        .btn-add {
          background-color: transparent !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
        }
        .btn-add:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
        }
        .table-responsive {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          overflow-x: auto;
        }
        .table {
          color: #ffffff !important;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: transparent !important;
        }
        .table * {
          background-color: transparent !important;
        }
        .table th {
          color: rgba(255, 255, 255, 0.7) !important;
          border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
          padding: 16px !important;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          text-align: left;
        }
        .table td {
          padding: 16px !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #ffffff !important;
        }
        .table tr {
          transition: background-color 0.2s;
        }
        .table tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.03) !important;
        }
        .btn-link {
          background: none;
          border: none;
          padding: 0;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: opacity 0.2s;
          margin-right: 12px;
        }
        .btn-link:hover {
          opacity: 0.7;
        }
        .text-blue {
          color: #60a5fa;
        }
        .text-red {
          color: #f87171;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          overflow-y: auto;
          padding: 20px;
        }
        .modal-dialog {
          background-color: rgba(26, 26, 26, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        .modal-header {
          padding: 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .modal-header h3 {
          margin: 0;
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s;
        }
        .modal-close:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }
        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }
        .modal-footer {
          padding: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 24px;
        }
        .tab {
          padding: 12px 24px;
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }
        .tab:hover {
          color: rgba(255, 255, 255, 0.8);
        }
        .tab.active {
          color: #4f46e5;
          border-bottom-color: #4f46e5;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-label {
          display: block;
          margin-bottom: 8px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
          font-size: 0.875rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        .form-row-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        input[type="range"] {
          width: 100%;
          height: 6px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
          outline: none;
          -webkit-appearance: none;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: #4f46e5;
          border-radius: 50%;
          cursor: pointer;
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: #4f46e5;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }
        .range-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
        }
        .range-label {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
        }
        .highlight-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .highlight-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          margin-bottom: 8px;
        }
        .btn-secondary {
          background-color: transparent;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
        }
        .btn-danger {
          background-color: #ef4444;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-danger:hover {
          background-color: #dc2626;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .range-value-label {
          color: #ffffff !important;
          font-weight: 600;
        }
        .loading-spinner {
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-top-color: rgba(255, 255, 255, 0.8);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .text-center {
          text-align: center;
        }
        .my-4 {
          margin: 2rem 0;
        }
        .mt-2 {
          margin-top: 0.5rem;
        }
        .mb-3 {
          margin-bottom: 1rem;
        }
        textarea.form-control {
          min-height: 100px;
          resize: vertical;
        }
        .score-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: #4f46e5;
        }
        .section-divider {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding-bottom: 16px;
          margin-bottom: 16px;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .no-data {
          text-align: center;
          padding: 3rem;
          color: rgba(255, 255, 255, 0.6);
        }
      `}</style>

      {/* Feedback message */}
      {feedbackMessage.message && (
        <div className={feedbackMessage.type === 'success' ? 'alert-success' : 'alert-danger'}>
          <span>{feedbackMessage.message}</span>
          <button 
            className="close-btn"
            onClick={() => setFeedbackMessage({ type: '', message: '' })}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Search and Add controls */}
      <div className="filter-header">
        <input
          type="text"
          placeholder="Search neighborhoods..."
          className="form-control"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '300px' }}
        />
        
        <button 
          className="btn-add"
          onClick={handleAddNeighborhood}
        >
          + Add New Neighborhood
        </button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="alert-danger">
          <span>{error}</span>
          <button 
            className="btn-secondary btn-sm"
            onClick={fetchNeighborhoods}
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Loading spinner */}
      {loading && !showModal && (
        <div className="text-center my-4">
          <div className="loading-spinner"></div>
          <p className="mt-2">Loading neighborhoods...</p>
        </div>
      )}
      
      {/* Neighborhoods table */}
      {!loading && (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Scores</th>
                <th>Schools</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNeighborhoods.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No neighborhoods found
                  </td>
                </tr>
              ) : (
                filteredNeighborhoods.map((neighborhood) => (
                  <tr key={neighborhood._id}>
                    <td>{neighborhood.name}</td>
                    <td>{neighborhood.city}, {neighborhood.state}</td>
                    <td>
                      Walk: {neighborhood.walkScore} | Transit: {neighborhood.transitScore} | Bike: {neighborhood.bikeScore}
                    </td>
                    <td>
                      {neighborhood.schools?.elementary?.name ? '✓ Elementary' : ''}
                      {neighborhood.schools?.middle?.name ? ' ✓ Middle' : ''}
                      {neighborhood.schools?.high?.name ? ' ✓ High' : ''}
                    </td>
                    <td>
                      <button 
                        className="btn-link text-blue"
                        onClick={() => handleEditNeighborhood(neighborhood._id)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-link text-red"
                        onClick={() => {
                          setFormData({ ...formData, id: neighborhood._id, name: neighborhood.name });
                          setShowDeleteConfirm(true);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') setShowModal(false);
        }}>
          <div className="modal-dialog">
            <div className="modal-header">
              <h3>{isEditing ? 'Edit Neighborhood' : 'Create New Neighborhood'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* Tabs */}
              <div className="tabs">
                <button 
                  className={`tab ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  Details
                </button>
                <button 
                  className={`tab ${activeTab === 'scores' ? 'active' : ''}`}
                  onClick={() => setActiveTab('scores')}
                >
                  Scores
                </button>
                <button 
                  className={`tab ${activeTab === 'schools' ? 'active' : ''}`}
                  onClick={() => setActiveTab('schools')}
                >
                  Schools
                </button>
                <button 
                  className={`tab ${activeTab === 'highlights' ? 'active' : ''}`}
                  onClick={() => setActiveTab('highlights')}
                >
                  Highlights
                </button>
              </div>
              
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label className="form-label">Neighborhood Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Capitol Hill"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Denver"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. CO"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      placeholder="Describe the neighborhood..."
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows="4"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://..."
                      value={formData.image}
                      onChange={(e) => handleInputChange('image', e.target.value)}
                    />
                  </div>
                </div>
              )}
              
              {/* Scores Tab */}
              {activeTab === 'scores' && (
                <div>
                  <div className="form-group">
                    <label className="form-label range-value-label">
                      Walk Score: <span className="score-value">{formData.walkScore}</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.walkScore}
                      onChange={(e) => handleInputChange('walkScore', parseInt(e.target.value))}
                    />
                    <div className="range-labels">
                      <span className="range-label">0 - Car-Dependent</span>
                      <span className="range-label">100 - Walker's Paradise</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label range-value-label">
                      Transit Score: <span className="score-value">{formData.transitScore}</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.transitScore}
                      onChange={(e) => handleInputChange('transitScore', parseInt(e.target.value))}
                    />
                    <div className="range-labels">
                      <span className="range-label">0 - Minimal Transit</span>
                      <span className="range-label">100 - Rider's Paradise</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label range-value-label">
                      Bike Score: <span className="score-value">{formData.bikeScore}</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.bikeScore}
                      onChange={(e) => handleInputChange('bikeScore', parseInt(e.target.value))}
                    />
                    <div className="range-labels">
                      <span className="range-label">0 - Not Bikeable</span>
                      <span className="range-label">100 - Biker's Paradise</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Schools Tab */}
              {activeTab === 'schools' && (
                <div>
                  <div className="section-divider">Elementary School</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">School Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Elementary School Name"
                        value={formData.schools.elementary.name}
                        onChange={(e) => handleSchoolChange('elementary', 'name', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label range-value-label">
                        Rating: <span className="score-value">{formData.schools.elementary.rating}</span>/10
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={formData.schools.elementary.rating}
                        onChange={(e) => handleSchoolChange('elementary', 'rating', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  
                  <div className="section-divider">Middle School</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">School Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Middle School Name"
                        value={formData.schools.middle.name}
                        onChange={(e) => handleSchoolChange('middle', 'name', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label range-value-label">
                        Rating: <span className="score-value">{formData.schools.middle.rating}</span>/10
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={formData.schools.middle.rating}
                        onChange={(e) => handleSchoolChange('middle', 'rating', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  
                  <div className="section-divider">High School</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">School Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="High School Name"
                        value={formData.schools.high.name}
                        onChange={(e) => handleSchoolChange('high', 'name', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label range-value-label">
                        Rating: <span className="score-value">{formData.schools.high.rating}</span>/10
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={formData.schools.high.rating}
                        onChange={(e) => handleSchoolChange('high', 'rating', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Highlights Tab */}
              {activeTab === 'highlights' && (
                <div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem' }}>
                    Add highlights that will appear on property details for properties in this neighborhood.
                  </p>
                  
                  {/* Current highlights */}
                  <div className="mb-3">
                    <div className="section-divider">Current Highlights</div>
                    {formData.highlights.length === 0 ? (
                      <p style={{ color: 'rgba(255, 255, 255, 0.5)' }}>No highlights added yet.</p>
                    ) : (
                      <ul className="highlight-list">
                        {formData.highlights.map((highlight, index) => (
                          <li key={index} className="highlight-item">
                            <span>{highlight.icon} - {highlight.text}</span>
                            <button 
                              className="btn-danger btn-sm"
                              onClick={() => handleRemoveHighlight(index)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  {/* Add new highlight */}
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.05)', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(255, 255, 255, 0.1)' 
                  }}>
                    <div className="section-divider">Add New Highlight</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Icon</label>
                        <select
                          className="form-control"
                          value={highlightIcon}
                          onChange={(e) => setHighlightIcon(e.target.value)}
                        >
                          {iconOptions.map(icon => (
                            <option key={icon} value={icon}>
                              {icon}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Text</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Close to parks and trails"
                          value={highlightText}
                          onChange={(e) => setHighlightText(e.target.value)}
                        />
                      </div>
                    </div>
                    <button 
                      className="btn-add"
                      onClick={handleAddHighlight}
                      disabled={!highlightText.trim()}
                    >
                      Add Highlight
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditing ? 'Update Neighborhood' : 'Create Neighborhood')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') setShowDeleteConfirm(false);
        }}>
          <div className="modal-dialog" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete the neighborhood "{formData.name}"?</p>
              <p style={{ color: '#f87171', marginTop: '1rem' }}>
                <strong>Warning:</strong> This action cannot be undone. Properties linked to this neighborhood will be affected.
              </p>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={() => handleDeleteNeighborhood(formData.id)}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NeighborhoodManagementTab;